import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Logo, Wordmark, Footer } from "../components/Layout";
import {
  BookOpen, Users, Shield, GraduationCap, ArrowRight,
  CheckCircle, BarChart3, Award, HelpCircle, MessageCircle, Heart,
  Compass, Search as SearchIcon, Briefcase, Sparkles, Rocket
} from "lucide-react";
import StudentLocationsMap from "../components/StudentLocationsMap";

const FEATURE_ICONS = [BookOpen, Users, Shield, GraduationCap];
const WHY_ICONS = [CheckCircle, Award, Users];

const PATHWAY_STEPS = [
  {
    key: "discover",
    label: "Discover",
    icon: Compass,
    accent: "#F5C518",        // amber/gold
    accentSoft: "rgba(245, 197, 24, 0.18)",
    body: "We're glad you found us! Find out more about the Aspiritech Academy and discover yourself along the way.",
  },
  {
    key: "uncover",
    label: "Uncover",
    icon: SearchIcon,
    accent: "#F59E42",        // warm orange
    accentSoft: "rgba(245, 158, 66, 0.18)",
    body: "We'll find out more about you through our intake process, including uncovering your strengths and potential.",
  },
  {
    key: "learn",
    label: "Learn",
    icon: GraduationCap,
    accent: "#EC4899",        // magenta/pink
    accentSoft: "rgba(236, 72, 153, 0.18)",
    body: "Once you choose a program, you'll learn real-world applicable skills and earn an industry-recognized credential.",
  },
  {
    key: "earn",
    label: "Earn",
    icon: Briefcase,
    accent: "#60A5FA",        // sky blue
    accentSoft: "rgba(96, 165, 250, 0.18)",
    body: "We'll focus on getting you ready for work experience through internships, apprenticeships, on-the-job-training and much more.",
  },
  {
    key: "belong",
    label: "Belong",
    icon: Sparkles,
    accent: "#A78BFA",        // soft purple
    accentSoft: "rgba(167, 139, 250, 0.18)",
    body: "Once you're in the Aspiritech community, we're here for you—for life. Career journeys have ups and downs. We're the support you need every step of the way.",
  },
];

const LandingPage = () => {
  const { user, login } = useAuth();
  const location = useLocation();
  const [content, setContent] = useState(null);

  useEffect(() => {
    fetch(`${API}/site-content`)
      .then(res => res.ok ? res.json() : null)
      .then(data => setContent(data))
      .catch(() => {});
  }, []);

  // When the page is opened with #mentor-cta (e.g., from a "Become A Mentor" link),
  // smoothly scroll to that section.
  useEffect(() => {
    if (location.hash !== "#mentor-cta") return;
    const tryScroll = () => {
      const el = document.querySelector('[data-testid="mentor-cta-section"]');
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    const t = setTimeout(tryScroll, 350);
    return () => clearTimeout(t);
  }, [location.hash]);

  const hero = content?.hero || {};
  const stats = content?.stats || [];
  const features = content?.features || {};
  const whySection = content?.why_section || {};
  const cta = content?.cta || {};

  // Render title with line breaks
  const renderTitle = (text) => {
    if (!text) return null;
    return text.split("\n").map((line, i) => (
      <span key={i}>{line}{i < text.split("\n").length - 1 && <br />}</span>
    ));
  };

  return (
    <div className="min-h-screen bg-surface-app">
      {/* Floating Nav */}
      <header className="sticky top-0 w-full bg-white/95 backdrop-blur-sm border-b border-slate-200/80 z-50 px-6 py-3" data-testid="landing-header">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <Wordmark tone="dark" size="md" />
          </Link>
          <nav className="hidden md:flex items-center gap-6">
            {user ? (
              <>
                <Link to="/programs" className="nav-link text-sm font-medium">Browse Programs</Link>
                <Link to="/skills-matrix" className="nav-link text-sm font-medium">Search Talent</Link>
              </>
            ) : (
              <>
                <Link to="/apply" className="nav-link text-sm font-medium" data-testid="landing-enroll-link">Enroll Now</Link>
                <Link to="/skills-matrix" className="nav-link text-sm font-medium" data-testid="landing-talent-link">Search Talent</Link>
                <Link to="/programs" className="nav-link text-sm font-medium" data-testid="landing-programs-link">Browse Programs</Link>
                <Link to="/workforce-lookup" className="nav-link text-sm font-medium" data-testid="landing-workforce-link">Workforce Boards</Link>
                <Link to="/faq" className="nav-link text-sm font-medium" data-testid="landing-faq-link">FAQ</Link>
                <Link to="/signup" className="nav-link text-sm font-medium" data-testid="landing-signup-link">Sign Up</Link>
              </>
            )}
          </nav>
          <div className="flex items-center gap-4">
            {user ? (
              <Link to="/dashboard">
                <Button className="btn-primary" data-testid="landing-dashboard-btn">Go to Dashboard</Button>
              </Link>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/signup">
                  <Button variant="outline" className="text-sm border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#1E3A8A]/5" data-testid="landing-signup-btn">
                    Sign Up
                  </Button>
                </Link>
                <Link to="/login">
                  <Button className="btn-accent" data-testid="landing-signin-btn">
                    Sign In
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-brand-navy text-white pt-20 pb-20 md:pt-28 md:pb-28 px-6 lg:px-8 relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-gradient-to-br from-black/10 to-transparent" />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }} />
        <div className="max-w-5xl mx-auto text-center relative">
          <div className="flex justify-center mb-8">
            <Logo variant="dark" height={140} />
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold leading-tight mb-6 tracking-tight">
            {renderTitle(hero.title || "Launch Your Career\nBe Limitless")}
          </h1>
          <p className="text-white/70 text-lg sm:text-xl max-w-2xl mx-auto mb-10 font-body leading-relaxed">
            {hero.subtitle || "We offer career training in tech and related fields designed specifically to uplift the potential and confidence of autistic and neurodivergent learners. Build real skills, earn certifications, and connect with employers."}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            {user ? (
              <Link to="/apply">
                <Button className="btn-accent text-lg px-8 py-3" data-testid="hero-enroll-btn">
                  {hero.cta_text_auth || "Start Enrollment"} <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            ) : (
              <Link to="/apply">
                <Button className="btn-accent text-lg px-8 py-3" data-testid="hero-signin-btn">
                  {hero.cta_text || "Get Started"} <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            )}
            <Link to="/skills-matrix">
              <Button className="bg-white/10 hover:bg-white/20 text-white border-2 border-white/40 px-6 py-3 rounded-xl font-medium" data-testid="hero-matrix-btn">
                <BarChart3 className="w-5 h-5 mr-2" /> {hero.secondary_btn_text || "Search Talent"}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Row */}
      <section className="max-w-5xl mx-auto px-6 -mt-12 relative z-10" data-testid="stats-section">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {(stats.length ? stats : [{value:"",label:""}]).map((stat, i) => (
            <Card key={i} className="card-surface text-center card-hover" data-testid={`stat-${i}`}>
              <CardContent className="pt-6 pb-4">
                <p className="text-3xl font-heading font-bold text-brand-navy">{stat.value}</p>
                <p className="text-sm text-slate-500 mt-1">{stat.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Aspiritech Pathway: Discover -> Uncover -> Learn -> Earn -> Belong */}
      <section
        className="relative overflow-hidden mt-24"
        data-testid="pathway-section"
        style={{
          background: "radial-gradient(ellipse at 50% 100%, rgba(120, 60, 200, 0.35) 0%, rgba(15, 27, 61, 1) 45%, #060B23 100%)",
        }}
      >
        {/* Star-field overlay */}
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-60 pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(1px 1px at 20px 30px, rgba(255,255,255,0.7), transparent), " +
              "radial-gradient(1px 1px at 80px 120px, rgba(255,255,255,0.5), transparent), " +
              "radial-gradient(1.5px 1.5px at 160px 60px, rgba(255,255,255,0.6), transparent), " +
              "radial-gradient(1px 1px at 250px 200px, rgba(255,255,255,0.5), transparent), " +
              "radial-gradient(1px 1px at 340px 90px, rgba(255,255,255,0.8), transparent), " +
              "radial-gradient(1px 1px at 420px 280px, rgba(255,255,255,0.4), transparent), " +
              "radial-gradient(1.5px 1.5px at 520px 150px, rgba(255,255,255,0.6), transparent), " +
              "radial-gradient(1px 1px at 620px 40px, rgba(255,255,255,0.7), transparent), " +
              "radial-gradient(1px 1px at 720px 220px, rgba(255,255,255,0.5), transparent), " +
              "radial-gradient(1.5px 1.5px at 820px 110px, rgba(255,255,255,0.6), transparent)",
            backgroundSize: "900px 320px",
          }}
        />

        {/* Decorative planet (top-left moon) */}
        <div aria-hidden="true" className="absolute top-12 left-10 w-20 h-20 rounded-full opacity-50"
          style={{
            background: "radial-gradient(circle at 35% 35%, #E2E8F0 0%, #94A3B8 60%, #475569 100%)",
            boxShadow: "0 0 60px rgba(226, 232, 240, 0.2)",
          }}
        />
        {/* Decorative planet (top-right saturn-ish) */}
        <div aria-hidden="true" className="absolute top-24 right-12 w-24 h-24 rounded-full opacity-40 hidden md:block"
          style={{
            background: "radial-gradient(circle at 30% 30%, #818CF8 0%, #4F46E5 60%, #312E81 100%)",
            boxShadow: "0 0 80px rgba(129, 140, 248, 0.3)",
          }}
        />

        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-24">
          <div className="text-center mb-12">
            <span className="text-brand-gold text-xs uppercase tracking-[0.35em] font-bold">
              The Aspiritech Path
            </span>
            <h2 className="text-3xl sm:text-5xl lg:text-6xl font-heading font-bold text-white mt-3 mb-5 tracking-tight">
              From first hello to lifelong belonging
            </h2>
            <p className="text-white/70 max-w-2xl mx-auto leading-relaxed">
              Every participant moves through five stages designed around how autistic adults
              actually grow careers — at their own pace, with support that doesn't expire.
            </p>
          </div>

          {/* Rocket-arc decorative SVG — sits behind the pillars (desktop only) */}
          <div className="relative">
            <svg
              aria-hidden="true"
              viewBox="0 0 1200 220"
              className="hidden lg:block absolute left-0 right-0 -top-2 w-full h-32 pointer-events-none"
              preserveAspectRatio="none"
            >
              <defs>
                <linearGradient id="rocket-arc" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#F5C518" />
                  <stop offset="25%" stopColor="#F59E42" />
                  <stop offset="50%" stopColor="#EC4899" />
                  <stop offset="75%" stopColor="#60A5FA" />
                  <stop offset="100%" stopColor="#A78BFA" />
                </linearGradient>
              </defs>
              <path
                d="M 100 180 Q 600 -40 1100 180"
                fill="none"
                stroke="url(#rocket-arc)"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeDasharray="6 8"
                opacity="0.85"
              />
              {/* Glowing dots at each step waypoint */}
              {[120, 360, 600, 840, 1080].map((cx, i) => {
                const colors = ["#F5C518", "#F59E42", "#EC4899", "#60A5FA", "#A78BFA"];
                const y = i === 2 ? 0 : (i === 1 || i === 3 ? 60 : 130);
                return (
                  <circle key={i} cx={cx} cy={y + 30} r="4" fill={colors[i]}
                    style={{ filter: `drop-shadow(0 0 6px ${colors[i]})` }} />
                );
              })}
              {/* Rocket icon centered above step 3 */}
              <g transform="translate(580, -10)">
                <text fontSize="48" fill="#fff" style={{ filter: "drop-shadow(0 0 8px rgba(236, 72, 153, 0.8))" }}>🚀</text>
              </g>
            </svg>

            {/* Pillars grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 lg:gap-3 relative">
              {PATHWAY_STEPS.map((step, i) => {
                const Icon = step.icon;
                return (
                  <div
                    key={step.key}
                    className="relative group"
                    data-testid={`pathway-step-${step.key}`}
                    style={{ animationDelay: `${i * 100}ms` }}
                  >
                    {/* Numbered ring badge floating above the pillar */}
                    <div
                      className="absolute -top-5 left-1/2 -translate-x-1/2 w-10 h-10 rounded-full flex items-center justify-center font-heading font-bold text-base z-10"
                      style={{
                        background: "rgba(6, 11, 35, 0.9)",
                        color: step.accent,
                        border: `2px solid ${step.accent}`,
                        boxShadow: `0 0 24px ${step.accentSoft}`,
                      }}
                    >
                      {i + 1}
                    </div>

                    {/* Glowing pillar */}
                    <div
                      className="rounded-[28px] px-4 pt-9 pb-6 h-full flex flex-col items-center text-center backdrop-blur-sm transition-transform duration-500 group-hover:-translate-y-2"
                      style={{
                        background: `linear-gradient(180deg, ${step.accentSoft} 0%, rgba(15, 27, 61, 0.55) 40%, rgba(6, 11, 35, 0.85) 100%)`,
                        border: `1px solid ${step.accent}55`,
                        boxShadow: `0 0 40px ${step.accentSoft}, inset 0 0 24px ${step.accent}11`,
                        minHeight: "340px",
                      }}
                    >
                      {/* Icon */}
                      <div
                        className="w-14 h-14 rounded-2xl flex items-center justify-center mb-3"
                        style={{
                          background: `${step.accent}22`,
                          boxShadow: `0 0 24px ${step.accent}55, inset 0 0 12px ${step.accent}33`,
                        }}
                      >
                        <Icon className="w-7 h-7" style={{ color: step.accent }} strokeWidth={2} />
                      </div>

                      {/* Label */}
                      <p
                        className="font-heading font-bold tracking-wide uppercase text-base mb-3"
                        style={{
                          color: step.accent,
                          textShadow: `0 0 12px ${step.accent}77`,
                        }}
                      >
                        {step.label}
                      </p>

                      {/* Body */}
                      <p className="text-white/85 text-xs sm:text-sm leading-relaxed">
                        {step.body}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Bottom tagline pill */}
          <div className="mt-14 flex justify-center" data-testid="pathway-tagline">
            <div
              className="inline-flex items-center gap-3 rounded-full px-6 py-3 text-sm sm:text-base"
              style={{
                background: "rgba(6, 11, 35, 0.65)",
                border: "1px solid rgba(255, 213, 7, 0.4)",
                boxShadow: "0 0 40px rgba(255, 213, 7, 0.15)",
              }}
            >
              <Rocket className="w-5 h-5 text-brand-gold shrink-0" />
              <span className="text-white/90 font-medium">
                <span className="text-brand-gold font-bold">Your future. Your pace. Lifelong support.</span>
                <span className="hidden sm:inline">  Let's launch together.</span>
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* "We Meet You Where You Are" — Public student-locations map */}
      <section className="bg-white py-20 px-6" data-testid="we-meet-you-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <span className="text-brand-navy text-xs uppercase tracking-[0.35em] font-bold">
              Coast to Coast
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-slate-900 mt-3 mb-4 tracking-tight">
              We meet you where you are
            </h2>
            <p className="text-slate-500 max-w-2xl mx-auto leading-relaxed">
              Wherever you live, we'll meet you online — and connect you with the people, programs,
              local workforce boards, and employers that can help you move forward.
            </p>
          </div>
          <StudentLocationsMap publicMode />
        </div>
      </section>

      {/* Features Grid */}
      <section className="max-w-6xl mx-auto px-6 py-24" data-testid="features-section">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900 mb-4">
            {features.heading || "Everything you need to succeed"}
          </h2>
          <p className="text-slate-500 max-w-xl mx-auto">
            {features.subtitle || "A complete platform for learning, networking, and launching your tech career."}
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {(features.items || []).map((feature, i) => {
            const Icon = FEATURE_ICONS[i % FEATURE_ICONS.length];
            return (
              <Card key={i} className="card-surface card-hover" data-testid={`feature-${i}`}>
                <CardContent className="p-8 flex gap-5">
                  <div className="w-12 h-12 rounded-xl bg-brand-navy/5 flex items-center justify-center shrink-0">
                    <Icon className="w-6 h-6 text-brand-navy" />
                  </div>
                  <div>
                    <h3 className="text-lg font-heading font-semibold text-slate-900 mb-2">{feature.title}</h3>
                    <p className="text-slate-500 leading-relaxed">{feature.description}</p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Why Aspiritech */}
      <section className="bg-surface-muted py-20 px-6" data-testid="why-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900 mb-6">
            {whySection.heading || "Why Aspiritech Academy?"}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 mt-12">
            {(whySection.items || []).map((item, i) => {
              const Icon = WHY_ICONS[i % WHY_ICONS.length];
              return (
                <div key={i} className="flex flex-col items-center gap-3">
                  <div className="w-14 h-14 rounded-full bg-brand-gold/10 flex items-center justify-center">
                    <Icon className="w-7 h-7 text-brand-navy" />
                  </div>
                  <p className="text-slate-700 font-medium">{typeof item === "string" ? item : item.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Sign Up band */}
      {!user && (
        <section className="max-w-6xl mx-auto px-6 py-16" data-testid="homepage-signup-section">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <p className="text-[#1E3A8A] text-xs uppercase tracking-[0.2em] font-bold mb-3">Get Started</p>
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900">Sign up and join Aspiritech</h2>
            <p className="text-slate-600 mt-3">Choose your path — whether you're launching your tech career or ready to mentor someone who is.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="border border-slate-200 hover:border-[#1E3A8A] transition-colors">
              <CardContent className="p-8 flex flex-col h-full">
                <div className="w-14 h-14 rounded-2xl bg-[#1E3A8A]/10 flex items-center justify-center mb-5">
                  <GraduationCap className="w-7 h-7 text-[#1E3A8A]" />
                </div>
                <h3 className="text-2xl font-heading font-bold text-slate-900">I'm a Jobseeker</h3>
                <p className="text-slate-600 mt-2 flex-1">Apply to the Academy, share your resume with employers, or connect with our community.</p>
                <Link to="/signup" className="mt-6" data-testid="home-signup-jobseeker">
                  <Button className="btn-primary w-full h-11">Get started <ArrowRight className="w-4 h-4 ml-1" /></Button>
                </Link>
              </CardContent>
            </Card>
            <Card className="border border-slate-200 hover:border-[#FFD507] transition-colors">
              <CardContent className="p-8 flex flex-col h-full">
                <div className="w-14 h-14 rounded-2xl bg-[#FFD507]/20 flex items-center justify-center mb-5">
                  <Heart className="w-7 h-7 text-[#0F1B3D]" />
                </div>
                <h3 className="text-2xl font-heading font-bold text-slate-900">I want to Mentor</h3>
                <p className="text-slate-600 mt-2 flex-1">Guide neurodivergent talent into tech careers. A few hours a month makes a lasting impact.</p>
                <Link to="/mentor-signup" className="mt-6" data-testid="home-signup-mentor">
                  <Button className="btn-primary w-full h-11">Become a mentor <ArrowRight className="w-4 h-4 ml-1" /></Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </section>
      )}

      {/* Become a Mentor CTA */}
      <section className="max-w-5xl mx-auto px-6 py-16" data-testid="mentor-cta-section">
        <Card className="overflow-hidden border-0 shadow-xl">
          <div className="grid md:grid-cols-2">
            <div className="p-10 md:p-12 flex flex-col justify-center bg-white">
              <span className="text-[#1E3A8A] text-xs uppercase tracking-[0.2em] font-bold mb-3">For Industry Pros</span>
              <h2 className="text-2xl sm:text-3xl font-heading font-bold text-slate-900 mb-4">
                Become a Mentor at Aspiritech Academy
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                Share what you know with neurodivergent professionals breaking into tech.
                Help shape someone's career trajectory in just a few hours a month — your expertise is the unlock.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/mentor-signup" data-testid="mentor-cta-btn">
                  <Button className="btn-primary px-6 py-2.5 rounded-xl flex items-center gap-2">
                    <Heart className="w-4 h-4" /> Apply to Mentor
                  </Button>
                </Link>
                <Link to="/mentor-guide">
                  <Button variant="outline" className="px-6 py-2.5 rounded-xl" data-testid="mentor-cta-learn-more">
                    How to be a great mentor
                  </Button>
                </Link>
              </div>
            </div>
            <div className="bg-[#FFD507] p-10 md:p-12 text-[#0F1B3D] flex flex-col justify-center relative overflow-hidden">
              <div className="relative">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-12 h-12 rounded-2xl bg-[#0F1B3D]/10 flex items-center justify-center">
                    <Heart className="w-6 h-6 text-[#0F1B3D]" />
                  </div>
                  <p className="font-heading text-lg font-bold text-[#0F1B3D]">A few hours, lasting impact</p>
                </div>
                <ul className="space-y-3 text-[#0F1B3D] text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 shrink-0 text-[#0F1B3D]" />
                    <span>Match with mentees in your field of expertise</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 shrink-0 text-[#0F1B3D]" />
                    <span>Flexible scheduling that fits your day job</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 shrink-0 text-[#0F1B3D]" />
                    <span>Guidance on neurodivergent-friendly mentoring</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 shrink-0 text-[#0F1B3D]" />
                    <span>Help build the future of inclusive tech</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </Card>
      </section>

      {/* Talent-only signup CTA */}
      <section className="max-w-5xl mx-auto px-6 py-12" data-testid="talent-cta-section">
        <Card className="overflow-hidden border border-slate-200">
          <div className="grid md:grid-cols-[1fr_360px]">
            <div className="p-8 md:p-10">
              <p className="text-[#1E3A8A] text-xs uppercase tracking-[0.2em] font-semibold mb-2">Talent Database</p>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-slate-900">
                Not ready to enroll? Get your profile in front of employers.
              </h2>
              <p className="text-slate-600 mt-3 max-w-xl">
                Create a free Talent Database profile — share your resume, skills, and Working Style — without
                applying to the Academy. You can apply later anytime.
              </p>
              <div className="flex flex-wrap gap-3 mt-6">
                <Link to="/talent-signup">
                  <Button className="btn-primary px-6 py-2.5 rounded-xl" data-testid="landing-talent-signup-btn">
                    Create Talent Profile <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/skills-matrix">
                  <Button variant="outline" className="px-6 py-2.5 rounded-xl">
                    See what employers see
                  </Button>
                </Link>
              </div>
            </div>
            <div className="bg-[#0F1B3D] text-white p-8 md:p-10 flex flex-col justify-center">
              <p className="text-[#FFD507] text-xs uppercase tracking-[0.2em] font-semibold mb-3">No cost. No application.</p>
              <ul className="space-y-3 text-white/90 text-sm">
                <li className="flex gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0 text-[#FFD507]" /> Upload your resume after signup</li>
                <li className="flex gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0 text-[#FFD507]" /> Take the Working Style Quiz</li>
                <li className="flex gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0 text-[#FFD507]" /> Toggle employer visibility anytime</li>
                <li className="flex gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0 text-[#FFD507]" /> Become part of the Aspiritech community</li>
              </ul>
            </div>
          </div>
        </Card>
      </section>

      {/* FAQ Callout */}
      <section className="max-w-5xl mx-auto px-6 py-16" data-testid="faq-callout-section">
        <Card className="overflow-hidden border-0 shadow-xl">
          <div className="grid md:grid-cols-2">
            <div className="bg-gradient-to-br from-[#1E3A8A] to-[#0F1B3D] p-10 md:p-12 text-white flex flex-col justify-center">
              <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center mb-6">
                <HelpCircle className="w-7 h-7 text-[#1E3A8A]" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-heading font-bold mb-4">Got Questions?</h2>
              <p className="text-white/70 leading-relaxed mb-6">
                From eligibility and costs to schedules and career support — we've answered everything. Plus, our AI assistant is always ready to help.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/faq">
                  <Button className="bg-[#FFD507] hover:bg-[#FFEC50] text-[#0F1B3D] font-semibold px-6 py-2.5 rounded-xl" data-testid="faq-callout-btn">
                    <HelpCircle className="w-4 h-4 mr-2" /> Browse FAQs
                  </Button>
                </Link>
              </div>
            </div>
            <div className="bg-white p-10 md:p-12 flex flex-col justify-center">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <MessageCircle className="w-4 h-4 text-[#1E3A8A]" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-800 text-sm">Do I need a diagnosis to apply?</p>
                    <p className="text-slate-500 text-xs mt-0.5">No formal diagnosis required — we welcome all neurodivergent adults.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-emerald-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <MessageCircle className="w-4 h-4 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-800 text-sm">Is there a cost?</p>
                    <p className="text-slate-500 text-xs mt-0.5">We work hard to cover tuition through WIOA and grants.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-amber-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <MessageCircle className="w-4 h-4 text-[#1E3A8A]" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-800 text-sm">Is the program remote?</p>
                    <p className="text-slate-500 text-xs mt-0.5">100% remote via Google Meet with full LMS access.</p>
                  </div>
                </div>
              </div>
              <p className="text-xs text-slate-400 mt-6">31 FAQs across 6 categories — or ask our LaunchPad AI assistant anytime.</p>
            </div>
          </div>
        </Card>
      </section>

      {/* CTA */}
      <section className="max-w-4xl mx-auto px-6 py-20 text-center" data-testid="cta-section">
        <h2 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900 mb-4">
          {cta.heading || "Ready to get started?"}
        </h2>
        <p className="text-slate-500 max-w-xl mx-auto mb-8">
          {cta.subtitle || "Join a community of neurodivergent tech professionals building careers they love."}
        </p>
        {user ? (
          <Link to="/apply">
            <Button className="btn-accent text-lg px-8 py-3" data-testid="cta-enroll-btn">
              {cta.btn_text_auth || "Start Your Application"} <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        ) : (
          <Link to="/apply">
            <Button className="btn-accent text-lg px-8 py-3" data-testid="cta-signin-btn">
              {cta.btn_text || "Start Your Application"} <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        )}
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default LandingPage;
